import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { Environment } from "../utils/Environment";
import { postWithUserTableParams } from "../services/fetchWrapper";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SuccessMessageComponent from "./SuccessMessageComponent";

const apiUrl = Environment.getApiUrl();

const ConfirmCostColumns: React.FC = () => {
    const username = localStorage.getItem("userName") || "";
    const [clientId, setClientId] = useState<string>("");
    const [loading, setLoading] = useState(true);

    // Column state
    const [possibleColumns, setPossibleColumns] = useState<{ column_name: string }[]>([]);
    const [possibleBaseColumns, setPossibleBaseColumns] = useState<string[]>([]);
    const [pipeLengthColumn, setPipeLengthColumn] = useState<string>("");
    const [confirmedDiameterColumn, setConfirmedDiameterColumn] = useState<string>("");
    const [materialColumn, setMaterialColumn] = useState<string>("");
    const [diameterInputValue, setDiameterInputValue] = useState<string>("");
    const [pipeLengthInputValue, setPipeLengthInputValue] = useState<string>("");
    // const [possibleMaterialColumns, setPossibleMaterialColumns] = useState<string[]>([]);

    const [baseGeometryColumn, setBaseGeometryColumn] = useState<string>("");

    const [uploadStatus, setUploadStatus] = useState<string | null>(null);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);

    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchColumns = async () => {
            const client_id = localStorage.getItem("client_id") || "";
            const job_id = localStorage.getItem("job_id") || "";
            try {
                const response = await postWithUserTableParams(`${apiUrl}/get-concretesteel-columns`, {
                    username,
                    client_id,
                    job_id,
                });
                if (!response.ok) return;
                const data = await response.json();
                if (data.possible_material_columns) {
                    const baseCols = (data.possible_material_columns.base_data || []).map(
                        (col: any) => typeof col === "string" ? col : col.column_name
                    );

                    setPossibleBaseColumns(baseCols);

                    setMaterialColumn(
                        baseCols.find((col: string) => /d_pipetype|pipetype|material/i.test(col)) || ""
                    );

                    // Try to auto-select geometry and material columns
                    setBaseGeometryColumn(
                        baseCols.find((col: string) => /geom|geometry/i.test(col)) || ""
                    );
                }
            } catch { }
        };
        fetchColumns();
    }, [apiUrl, username]);


    useEffect(() => {
        const storedClientId = localStorage.getItem("client_id") || "";
        setClientId(storedClientId);

        const fetchColumns = async () => {
            setLoading(true);
            try {
                // Get possible columns
                const resp = await postWithUserTableParams(`${apiUrl}/get-cost-columns`, {
                    username,
                    client_id: storedClientId,
                    job_id: localStorage.getItem("job_id") || "",
                });
                const data = await resp.json();
                if (Array.isArray(data.possible_material_columns)) {
                    const cols = data.possible_material_columns.map((c: any) =>
                        typeof c === "string" ? { column_name: c } : c
                    );
                    setPossibleColumns(cols);
                    // Auto-select pipe length
                    const lenCol = cols.find((c: any) => /len/i.test(c.column_name));
                    setPipeLengthColumn(lenCol ? lenCol.column_name : "");
                }

                // Get confirmed columns
                const resp2 = await postWithUserTableParams(`${apiUrl}/get-confirmed-columns`, {
                    username,
                    client_id: storedClientId,
                    job_id: localStorage.getItem("job_id") || "",
                });
                const data2 = await resp2.json();

                if (data2.diameter_column) setConfirmedDiameterColumn(data2.diameter_column);
                // else if (Array.isArray(data2.possible_material_columns)) {
                //     setPossibleMaterialColumns(data2.possible_material_columns);
                // }
            } catch (err) {
                setPossibleColumns([]);
            } finally {
                setLoading(false);
            }
        };

        if (storedClientId) fetchColumns();
    }, [username, apiUrl]);

    // Save confirmed columns to backend
    const handleSaveConfirmedColumns = async () => {
        setSaving(true);
        setShowSuccess(false);
        try {
            const client_id = localStorage.getItem("client_id") || "";
            const job_id = localStorage.getItem("job_id") || "";
            const columnsToSave = [
                {
                    client_id,
                    job_id,
                    column_name: baseGeometryColumn,
                    column_value: "",
                    flow_name: "geometry",
                    description: "base dataset column defining geometry type/shape data in gis file"
                },
                {
                    client_id: clientId,
                    job_id: localStorage.getItem("job_id") || "",
                    column_name: pipeLengthColumn,
                    column_value: "",
                    flow_name: "cost",
                    description: "User confirmed pipe length column for cost scoring"
                }
            ];
            if (confirmedDiameterColumn) {
                columnsToSave.push({
                    client_id: clientId,
                    job_id: localStorage.getItem("job_id") || "",
                    column_name: confirmedDiameterColumn,
                    column_value: "",
                    flow_name: "cost",
                    description: "Previously confirmed diameter column for cost scoring"
                });
            }
            if (materialColumn) {
                columnsToSave.push({
                    client_id: clientId,
                    job_id: localStorage.getItem("job_id") || "",
                    column_name: materialColumn,
                    column_value: "",
                    flow_name: "cost",
                    description: "Material column for cost scoring"
                });
            }
            const response = await fetch(`${apiUrl}/save-customer-calc-columns`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ columns: columnsToSave, client_id }),
            });
            if (!response.ok) throw new Error("Failed to save confirmed columns");
            localStorage.setItem("costColumnsConfirmed", "true");
            setUploadStatus("completed");
            setStatusMessage("Columns have been confirmed!");
            setShowSuccess(true);
        } catch (err) {
            alert("Failed to save columns");
            // eslint-disable-next-line no-console
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, textTransform: "uppercase" }}>
                Column Confirmation
            </Typography>
            <svg width="292" height="6" viewBox="0 0 292 6" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 16 }}>
                <path d="M0 3H292" stroke="#8AD019" strokeWidth="6" />
            </svg>
            <Typography variant="caption" sx={{ mb: 3, fontSize: 16 }}>
                Please confirm that the values for each of the four columns below are correct according to your base dataset.
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 14, textAlign: "center" }}>
                Pipe Diameter Column
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, mb: 3, width: "100%" }}>
                <Autocomplete
                    freeSolo
                    options={possibleColumns.map(c => c.column_name)}
                    value={confirmedDiameterColumn}
                    inputValue={diameterInputValue}
                    onInputChange={(_, v) => {
                        setDiameterInputValue(v);
                        setConfirmedDiameterColumn(v);
                    }}
                    onChange={(_, v) => {
                        setDiameterInputValue(v || "");
                        setConfirmedDiameterColumn(v || "");
                    }}
                    renderInput={params => <TextField {...params}
                        variant="outlined" required
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: "#F6FFE8",
                                height: 36,
                            },
                            "& .MuiOutlinedInput-input": {
                                padding: "8px 12px",
                            },
                        }}
                    />}
                    sx={{ width: 240, maxWidth: 240 }}
                />
                <Tooltip title="The confirmed pipe diameter field used for cost scoring.">
                    <IconButton size="small" sx={{ p: 0.25, mt: 0.25 }}>
                        <HelpOutlineIcon fontSize="small" sx={{ color: "#828282" }} />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 1 }}>
                <Typography variant="body2" sx={{ fontSize: 14, textAlign: "center" }}>
                    Pipe Material Column
                </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, mb: 3, width: "100%" }}>
                <Autocomplete
                    freeSolo
                    options={possibleBaseColumns}
                    value={materialColumn}
                    inputValue={materialColumn}
                    onInputChange={(_, v) => {
                        setMaterialColumn(v);
                    }}
                    onChange={(_, v) => {
                        setMaterialColumn(v || "");
                    }}
                    renderInput={params => <TextField {...params}
                        variant="outlined" required
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: "#F6FFE8",
                                height: 36,
                            },
                            "& .MuiOutlinedInput-input": {
                                padding: "8px 12px",
                            },
                        }} />}
                    sx={{ width: 240, maxWidth: 240 }}
                />
                <Tooltip title="The material/type field used to identify pipe composition.">
                    <IconButton size="small" sx={{ p: 0.25, mt: 0.25 }}>
                        <HelpOutlineIcon fontSize="small" sx={{ color: "#828282" }} />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 1 }}>
                <Typography variant="body2" sx={{ fontSize: 14, textAlign: "center" }}>
                    Pipe Length Column
                </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, mb: 3, width: "100%" }}>
                <Autocomplete
                    freeSolo
                    options={possibleColumns.map(c => c.column_name)}
                    value={pipeLengthColumn}
                    inputValue={pipeLengthInputValue}
                    onInputChange={(_, v) => {
                        setPipeLengthInputValue(v);
                        setPipeLengthColumn(v);
                    }}
                    onChange={(_, v) => {
                        setPipeLengthInputValue(v || "");
                        setPipeLengthColumn(v || "");
                    }}
                    renderInput={params => <TextField {...params}
                        variant="outlined" required
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: "#F6FFE8",
                                height: 36,
                            },
                            "& .MuiOutlinedInput-input": {
                                padding: "8px 12px",
                            },
                        }} />}
                    sx={{ width: 240, maxWidth: 240 }}
                />
                <Tooltip title="The pipe length field used in the cost calculation.">
                    <IconButton size="small" sx={{ p: 0.25, mt: 0.25 }}>
                        <HelpOutlineIcon fontSize="small" sx={{ color: "#828282" }} />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 1 }}>
                <Typography variant="body2" sx={{ fontSize: 14, textAlign: "center" }}>
                    Base Geometry Column
                </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, mb: 4, width: "100%" }}>
                <Autocomplete
                    freeSolo
                    options={possibleBaseColumns}
                    value={baseGeometryColumn}
                    onChange={(_, v) => setBaseGeometryColumn(v || "")}
                    renderInput={params => <TextField {...params}
                        variant="outlined"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: "#F6FFE8",
                                height: 36,
                            },
                            "& .MuiOutlinedInput-input": {
                                padding: "8px 12px",
                            },
                        }} />}
                    sx={{ width: 240, maxWidth: 240 }}
                />
                <Tooltip title="The geometry field from the base dataset used to identify shape/location information.">
                    <IconButton size="small" sx={{ p: 0.25, mt: 0.25 }}>
                        <HelpOutlineIcon fontSize="small" sx={{ color: "#828282" }} />
                    </IconButton>
                </Tooltip>
            </Box>
            <Button
                variant="contained"
                color="primary"
                onClick={handleSaveConfirmedColumns}
                sx={{ backgroundColor: "#0373B5" }}
            // disabled={saving || !pipeLengthColumn || (!materialColumn && possibleMaterialColumns.length > 0)}
            >
                {saving ? "Saving..." : "Save Columns"}
            </Button>
            {showSuccess && <SuccessMessageComponent message="Columns have been confirmed!" compact />}
            {uploadStatus && (
                <Box
                    sx={{
                        mt: 2,
                        p: 2,
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        maxWidth: 500,
                        mx: "auto",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h6">Processing Status</Typography>
                    <Typography variant="body2">
                        Status: {uploadStatus}
                    </Typography>
                    <Typography variant="body2">
                        Message: {statusMessage}
                    </Typography>
                </Box>
            )}
            {/* <Box sx={{ mb: 3, mt: 1, width: "100%", maxWidth: 500 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                    Example Data:
                </Typography>
                <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 2, textAlign: "left" }}>
                    <Typography variant="body2">
                        <b>Pipe Diameter Column:</b> <code>Value - 8</code>
                        <br />
                        <b>Material Column:</b> <code>Value - DI - DUCTILE IRON</code>
                        <br />
                        <b>Pipe Length Column:</b> <code>Value - 40.106</code>
                        <br />
                        <b>Base Geometry Column:</b> <code>Value - Your base dataset's geometry column</code>
                    </Typography>
                </Box>
            </Box> */}
        </Box>
    );
};

export default ConfirmCostColumns;